import { Play, Pause, Music, Clock, Download, ListPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { LikeButton, CommentSection } from '../SocialFeatures';
import { ShareButtons } from '../ShareButtons';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function TrackCover({ track, isCurrentTrack, isPlaying, onPlay }) {
  return (
    <div className="relative aspect-square max-w-md rounded-2xl overflow-hidden mb-8 group">
      <img 
        src={track.cover_url} 
        alt={track.title}
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <button
          onClick={onPlay}
          className="w-20 h-20 rounded-full bg-purple-500 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          data-testid="play-button"
        >
          {isCurrentTrack && isPlaying ? (
            <Pause className="w-8 h-8 text-white" />
          ) : (
            <Play className="w-8 h-8 text-white ml-1" />
          )}
        </button>
      </div>
    </div>
  );
}

export function TrackInfo({ track }) {
  return (
    <>
      <h1 className="text-3xl md:text-4xl font-bold mb-2">{track.title}</h1>
      <p className="text-xl text-gray-400 mb-4">{track.artist}</p>
      
      <div className="flex items-center gap-6 text-gray-400 mb-6">
        <div className="flex items-center gap-2">
          <Music className="w-4 h-4" />
          <span>{track.album}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          <span>{formatDuration(track.duration)}</span>
        </div>
      </div>
    </>
  );
}

export function TrackActions({ 
  track, 
  isAuthenticated, 
  hasPurchased, 
  downloading, 
  playlists, 
  showPlaylistDialog, 
  setShowPlaylistDialog,
  onAddToPlaylist,
  onDownload 
}) {
  return (
    <div className="flex flex-wrap gap-3 mb-8">
      <LikeButton contentType="track" contentId={track.id} />
      <ShareButtons title={track.title} type="track" />
      
      {isAuthenticated && (
        <Dialog open={showPlaylistDialog} onOpenChange={setShowPlaylistDialog}>
          <DialogTrigger asChild>
            <button className="btn-ghost flex items-center gap-2" data-testid="add-to-playlist-btn">
              <ListPlus className="w-4 h-4" /> Add to Playlist
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-white/10">
            <DialogHeader>
              <DialogTitle>Add to Playlist</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 mt-4">
              {playlists.length === 0 ? (
                <p className="text-gray-400 text-center py-4">No playlists yet. Create one first!</p>
              ) : (
                playlists.map(playlist => (
                  <button
                    key={playlist.id}
                    onClick={() => onAddToPlaylist(playlist.id)}
                    className="w-full p-3 rounded-lg bg-white/5 hover:bg-white/10 text-left"
                  >
                    <p className="font-medium">{playlist.name}</p>
                    <p className="text-sm text-gray-400">{playlist.track_ids?.length || 0} tracks</p>
                  </button>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {(hasPurchased || track.price === 0) && (
        <button 
          onClick={onDownload}
          disabled={downloading}
          className="btn-ghost flex items-center gap-2 text-green-400"
          data-testid="download-btn"
        >
          <Download className="w-4 h-4" />
          {downloading ? 'Downloading...' : 'Download'}
        </button>
      )}
    </div>
  );
}

export function BuyTrackCard({ track, hasPurchased, addingToCart, onAddToCart, onDownload, downloading }) {
  return (
    <div className="glass-card p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-400 mb-1">{track.price === 0 ? 'Free Download' : 'Buy Track'}</p>
          <p className="text-2xl font-bold text-purple-400">{track.price === 0 ? 'FREE' : `$${track.price.toFixed(2)}`}</p>
        </div>
        {hasPurchased ? (
          <span className="px-4 py-2 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
            Purchased ✓
          </span>
        ) : track.price === 0 ? (
          <button
            onClick={onDownload}
            disabled={downloading}
            className="btn-primary flex items-center gap-2"
            data-testid="free-download-btn"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Downloading...' : 'Download Free'}
          </button>
        ) : (
          <button
            onClick={onAddToCart}
            disabled={addingToCart}
            className="btn-primary flex items-center gap-2"
            data-testid="add-track-to-cart"
          >
            <ShoppingCart className="w-4 h-4" />
            Add to Cart
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500">
        {track.price === 0
          ? 'Free high-quality download, no purchase required.'
          : 'Purchase includes high-quality download and unlimited streaming access.'}
      </p>
    </div>
  );
}

import { ShoppingCart } from 'lucide-react';
